/* BK_SIZE_FITTER_v1
 * Bull Kicks size unit toggle (US / EU / UK) + ".0" suffix strip.
 *
 *  - Collection sidebar (mens-* / womens-* / kids-*): show toggle + rewrite labels.
 *  - Mixed parent collection (running, jordan-*, training-*, lifestyle-*, rescued):
 *    no toggle, just strip ".0".
 *  - PDP variant picker: show toggle + rewrite size button labels.
 *  - Choice persists in localStorage (bk-size-unit) sitewide.
 *  - Backend variant values UNCHANGED — only display text is rewritten.
 *
 * Conversion: Nike/Jordan chart (the dominant brand). Rounds reasonably for
 * other brands. Uses collection handle / product gender tag to disambiguate
 * the same numeric (e.g. Men US 8 vs Women US 8 vs Kids GS 8Y are all "8.0"
 * in Shopify but different real sizes).
 */
(function () {
  "use strict";

  const STORE_KEY = "bk-size-unit";
  const UNITS = ["US", "EU", "UK"];

  // ── Conversion charts ──────────────────────────────────────────────
  // Keys are normalized US strings (no trailing ".0", e.g. "8" not "8.0").
  // Values format US: { eu: "...", uk: "..." }.

  const CHART_M = {
    "5":    { eu: "37.5", uk: "4"   },
    "5.5":  { eu: "38",   uk: "4.5" },
    "6":    { eu: "38.5", uk: "5.5" },
    "6.5":  { eu: "39",   uk: "6"   },
    "7":    { eu: "40",   uk: "6"   },
    "7.5":  { eu: "40.5", uk: "6.5" },
    "8":    { eu: "41",   uk: "7"   },
    "8.5":  { eu: "42",   uk: "7.5" },
    "9":    { eu: "42.5", uk: "8"   },
    "9.5":  { eu: "43",   uk: "8.5" },
    "10":   { eu: "44",   uk: "9"   },
    "10.5": { eu: "44.5", uk: "9.5" },
    "11":   { eu: "45",   uk: "10"  },
    "11.5": { eu: "45.5", uk: "10.5"},
    "12":   { eu: "46",   uk: "11"  },
    "12.5": { eu: "46.5", uk: "11.5"},
    "13":   { eu: "47.5", uk: "12"  },
    "14":   { eu: "48.5", uk: "13"  },
    "15":   { eu: "49.5", uk: "14"  },
  };

  const CHART_W = {
    "5":    { eu: "35.5", uk: "2.5" },
    "5.5":  { eu: "36",   uk: "3"   },
    "6":    { eu: "36.5", uk: "3.5" },
    "6.5":  { eu: "37.5", uk: "4"   },
    "7":    { eu: "38",   uk: "4.5" },
    "7.5":  { eu: "38.5", uk: "5"   },
    "8":    { eu: "39",   uk: "5.5" },
    "8.5":  { eu: "40",   uk: "6"   },
    "9":    { eu: "40.5", uk: "6.5" },
    "9.5":  { eu: "41",   uk: "7"   },
    "10":   { eu: "42",   uk: "7.5" },
    "10.5": { eu: "42.5", uk: "8"   },
    "11":   { eu: "43",   uk: "8.5" },
    "12":   { eu: "44",   uk: "9.5" },
  };

  // Kids Grade-School (no suffix). Plain numbers 1..7 in a kids context.
  const CHART_K_GS = {
    "1":    { eu: "32.5", uk: "13.5"},
    "1.5":  { eu: "33",   uk: "1"   },
    "2":    { eu: "33.5", uk: "1"   },
    "2.5":  { eu: "34.5", uk: "1.5" },
    "3":    { eu: "35",   uk: "2"   },
    "3.5":  { eu: "35.5", uk: "2.5" },
    "4":    { eu: "36",   uk: "3"   },
    "4.5":  { eu: "36.5", uk: "3.5" },
    "5":    { eu: "37.5", uk: "4"   },
    "5.5":  { eu: "38",   uk: "4.5" },
    "6":    { eu: "38.5", uk: "5"   },
    "7":    { eu: "40",   uk: "6"   },
  };

  // Pre-school (Nike "C" suffix, e.g. 12C).
  const CHART_K_PS = {
    "8C":   { eu: "25",   uk: "7.5" },
    "9C":   { eu: "26",   uk: "8.5" },
    "10C":  { eu: "27",   uk: "9.5" },
    "11C":  { eu: "28",   uk: "10.5"},
    "12C":  { eu: "29.5", uk: "11.5"},
    "13C":  { eu: "31",   uk: "12.5"},
    "1C":   { eu: "32",   uk: "13"  },
    "2C":   { eu: "33",   uk: "1"   },
    "3C":   { eu: "35",   uk: "2"   },
  };

  // ── Context detection ──────────────────────────────────────────────

  function getContext() {
    const path = location.pathname.toLowerCase();
    // PDP — read JSON-LD or product data
    const m = path.match(/^\/products\/([^/?#]+)/);
    if (m) {
      // Look for Shopify analytics meta which has product tags / type
      const tagMeta = document.querySelector('meta[property="product:condition"], [data-product-tags]');
      const tagsRaw = (tagMeta && tagMeta.getAttribute("data-product-tags")) || "";
      const productTagAttr = document.querySelector("[data-bk-product-tags]");
      const tags = (productTagAttr ? productTagAttr.getAttribute("data-bk-product-tags") : tagsRaw) || "";
      const tagSet = new Set(tags.split(",").map((t) => t.trim()));
      if (tagSet.has("Women")) return { ctx: "W", show: true };
      if (tagSet.has("Kids")) return { ctx: "K", show: true };
      if (tagSet.has("Men") || tagSet.has("Unisex")) return { ctx: "M", show: true };
      // Fallback: title scan
      const title = document.title.toLowerCase();
      if (/women|wmns/.test(title)) return { ctx: "W", show: true };
      if (/kids|gs|td|ps/.test(title)) return { ctx: "K", show: true };
      return { ctx: "M", show: true };
    }
    // Collection
    const cm = path.match(/^\/collections\/([^/?#]+)/);
    if (cm) {
      const handle = decodeURIComponent(cm[1]);
      if (/^mens-|^men-|男裝/.test(handle)) return { ctx: "M", show: true };
      if (/^womens-|^women-|女裝/.test(handle)) return { ctx: "W", show: true };
      if (/^kids-|童裝/.test(handle)) return { ctx: "K", show: true };
      // Mixed/ambiguous (jordan-shoes, running, lifestyle, etc.) — show
      // toggle anyway; default to Men's chart since it's the dominant
      // catalog. Conversion is the same for both genders for the bulk of
      // common shoe sizes; edge cases will round to nearest US value.
      return { ctx: "M", show: true };
    }
    return { ctx: "M", show: false };
  }

  // ── Conversion ─────────────────────────────────────────────────────

  function normalizeUS(raw) {
    const s = String(raw).trim();
    // "7.0" → "7", "7.50" → "7.5", "12C" stays
    if (/^\d+\.0+$/.test(s)) return s.replace(/\.0+$/, "");
    if (/^\d+\.\d*?0+$/.test(s)) return s.replace(/0+$/, "");
    return s;
  }

  function convert(raw, unit, ctx) {
    const us = normalizeUS(raw);
    if (unit === "US") return us;
    let chart;
    if (/C$/.test(us)) chart = CHART_K_PS;
    else if (ctx === "W") chart = CHART_W;
    else if (ctx === "K") chart = CHART_K_GS;
    else chart = CHART_M;
    let entry = chart[us];
    // Cross-gender fallback: mixed collections default to ctx="M" but Shopify
    // surfaces variants from any gender; if the M chart doesn't have the
    // value, try W (and vice versa) so user gets a meaningful conversion.
    if (!entry && ctx === "M") entry = CHART_W[us];
    else if (!entry && ctx === "W") entry = CHART_M[us];
    if (!entry) return us; // unknown size → fall back to US
    return unit === "EU" ? entry.eu : entry.uk;
  }

  // Hide duplicate filter rows whose visible label matches an earlier row.
  // Caused by inconsistent backend data (e.g. "6" and "6.0" both stripping
  // to "6"). Sums the counts into the first row so the user sees a single
  // checkbox per displayed value.
  function dedupeSizeRows() {
    const inputs = document.querySelectorAll('input[name*="filter.v.option.size" i]');
    const seen = new Map();
    inputs.forEach((inp) => {
      const li = inp.closest("li");
      if (!li) return;
      const label = inp.closest("label");
      const valueEl = label && label.querySelector(".facet-checkbox__text-label");
      const text = valueEl && valueEl.textContent.trim();
      if (!text) return;
      const countMatch = label && label.textContent.match(/\((\d+)\)/);
      const count = countMatch ? parseInt(countMatch[1], 10) : 0;
      if (seen.has(text)) {
        const first = seen.get(text);
        first.count += count;
        // Update the first row's visible count
        const firstLabel = first.li.querySelector("label");
        const firstCountWalker = document.createTreeWalker(firstLabel, NodeFilter.SHOW_TEXT, null);
        let n;
        while ((n = firstCountWalker.nextNode())) {
          if (/\(\d+\)/.test(n.nodeValue)) {
            n.nodeValue = n.nodeValue.replace(/\(\d+\)/, `(${first.count})`);
            break;
          }
        }
        li.style.display = "none";
      } else {
        li.style.display = "";
        seen.set(text, { li, count });
      }
    });
  }

  // ── State ──────────────────────────────────────────────────────────

  function getUnit() {
    try {
      const v = localStorage.getItem(STORE_KEY);
      return UNITS.includes(v) ? v : "US";
    } catch {
      return "US";
    }
  }
  function setUnit(u) {
    try { localStorage.setItem(STORE_KEY, u); } catch {}
    document.dispatchEvent(new CustomEvent("bk:size-unit-change", { detail: { unit: u } }));
  }

  // ── DOM rewriting ──────────────────────────────────────────────────

  // Walk text nodes inside `el`, replace any node whose trimmed text === oldText.
  function replaceTextNode(el, oldText, newText) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const matches = [];
    let n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue && n.nodeValue.trim() === oldText) matches.push(n);
    }
    matches.forEach((node) => {
      node.nodeValue = node.nodeValue.replace(oldText, newText);
    });
    return matches.length > 0;
  }

  // For each input that looks like a size facet/variant, rewrite the label
  // text. Stamp data-bk-orig so subsequent rewrites can read the original
  // backend value rather than the previously-rewritten display text.
  function rewriteSizes(unit, ctx) {
    const inputs = document.querySelectorAll(
      'input[name*="filter.v.option.size" i], input[name="Size"], input[data-option-name="Size"]',
    );
    inputs.forEach((inp) => {
      let orig = inp.getAttribute("data-bk-orig");
      if (!orig) {
        orig = inp.value;
        inp.setAttribute("data-bk-orig", orig);
      }
      const display = convert(orig, unit, ctx);
      const label = inp.closest("label") || (inp.id && document.querySelector(`label[for="${CSS.escape(inp.id)}"]`));
      if (!label) return;
      // Try: match against normalized US first (cosmetic strip), then any prior unit display.
      const candidates = new Set([
        orig,
        normalizeUS(orig),
        convert(orig, "US", ctx),
        convert(orig, "EU", ctx),
        convert(orig, "UK", ctx),
      ]);
      candidates.delete(display);
      let replaced = false;
      candidates.forEach((cand) => {
        if (!replaced && cand) replaced = replaceTextNode(label, cand, display);
      });
    });
  }

  // ── Toggle UI ──────────────────────────────────────────────────────

  function buildToggle(unit) {
    const wrap = document.createElement("div");
    wrap.className = "bk-size-unit-toggle";
    wrap.setAttribute("role", "group");
    wrap.setAttribute("aria-label", "Size unit");
    UNITS.forEach((u) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bk-size-unit-btn" + (u === unit ? " is-active" : "");
      btn.textContent = u;
      btn.setAttribute("data-unit", u);
      wrap.appendChild(btn);
    });
    return wrap;
  }

  function updateToggleActive(unit) {
    document.querySelectorAll(".bk-size-unit-toggle .bk-size-unit-btn").forEach((b) => {
      b.classList.toggle("is-active", b.getAttribute("data-unit") === unit);
    });
  }

  // Event delegation — survives toggle re-injection / DOM swaps. Click
  // listeners attached to button elements get blown away by Shopify's facet
  // form re-renders; document-level delegation does not.
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target;
      if (!(t && t.closest)) return;
      const btn = t.closest(".bk-size-unit-btn");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      setUnit(btn.getAttribute("data-unit"));
    },
    true,
  );

  // Show toggle only when at least one numeric (shoe-style) size exists.
  // Prevents toggle showing on apparel-only collections where Size = XS/S/M/L.
  function hasShoeSizes(inputs) {
    for (const inp of inputs) {
      const v = (inp.getAttribute("data-bk-orig") || inp.value || "").trim();
      if (/^\d+(\.\d+)?C?$/i.test(v)) return true;
    }
    return false;
  }

  function injectToggle(ctx) {
    const unit = getUnit();

    // Idempotent: only insert toggle if the target wrapper doesn't already
    // contain one. Constant re-creation drops user clicks because Shopify's
    // facet form mutates the DOM during render and our MutationObserver
    // would otherwise destroy the button mid-click.
    if (!ctx.show) {
      document.querySelectorAll(".bk-size-unit-toggle").forEach((t) => t.remove());
      return;
    }

    // Collection sidebar size facet — insert AFTER <summary> so <details>
    // still has summary as its first child (HTML spec requirement).
    const facetInputs = document.querySelectorAll('input[name*="filter.v.option.size" i]');
    if (facetInputs.length && hasShoeSizes(facetInputs)) {
      const facetWrap = facetInputs[0].closest("details");
      if (facetWrap && !facetWrap.querySelector(":scope > .bk-size-unit-toggle")) {
        const summary = facetWrap.querySelector(":scope > summary");
        if (summary) summary.insertAdjacentElement("afterend", buildToggle(unit));
        else facetWrap.insertBefore(buildToggle(unit), facetWrap.firstChild);
      }
    }

    // PDP — find size variant fieldset/group; insert after its <legend>.
    const pdpInputs = document.querySelectorAll('input[name="Size"], input[data-option-name="Size"]');
    if (pdpInputs.length && hasShoeSizes(pdpInputs)) {
      const fs = pdpInputs[0].closest("fieldset") || pdpInputs[0].closest("variant-radios") || pdpInputs[0].parentElement;
      if (fs && !fs.querySelector(":scope > .bk-size-unit-toggle")) {
        const legend = fs.querySelector(":scope > legend");
        if (legend) legend.insertAdjacentElement("afterend", buildToggle(unit));
        else fs.insertBefore(buildToggle(unit), fs.firstChild);
      }
    }

    updateToggleActive(unit);
  }

  // ── Run / observe ──────────────────────────────────────────────────

  let scheduled = false;
  function scheduleRun() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      const ctx = getContext();
      const unit = getUnit();
      rewriteSizes(unit, ctx.ctx);
      dedupeSizeRows();
      injectToggle(ctx);
    });
  }

  document.addEventListener("DOMContentLoaded", scheduleRun);
  document.addEventListener("bk:size-unit-change", scheduleRun);

  // Watch for facet re-renders (Shopify Search & Discovery replaces sidebar
  // HTML on filter change) and PDP variant swaps.
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.addedNodes && m.addedNodes.length) {
        scheduleRun();
        return;
      }
    }
  });
  mo.observe(document.documentElement, { subtree: true, childList: true });
})();
